import {
  DocumentVersionSuggestions,
  HelpTip,
  useSuggestionDecisions,
  VersionSuggestionsReviewCard,
} from '@dataroom/shared';
import { useResolveVersionSuggestions, useVersionSuggestions } from '@/api/hooks';

/** The seller's decisions on the room's suggestions, through the seller portal's own hooks. */
function useSellerSuggestions(roomId: string) {
  const query = useVersionSuggestions(roomId);
  const resolve = useResolveVersionSuggestions(roomId);
  const decisions = useSuggestionDecisions(resolve.mutateAsync);
  return { suggestions: query.data, decisions };
}

interface ReviewProps {
  roomId: string;
  /** Open a document in the details drawer. */
  onOpenDocument?: (documentId: string) => void;
  /** Start expanded (tests); the page starts collapsed. */
  defaultExpanded?: boolean;
}

/**
 * The room's "Possible new versions" (ADR-0031) on the documents page: the same
 * compact list as the admin's — a one-line summary that opens strong-first rows,
 * each saying why it matched, with bulk Merge / Keep separate over the rows the
 * seller may decide. A row involving the room administrator's file says who
 * decides it and cannot be selected. Renders nothing when there is none.
 */
export function VersionSuggestionsReview({ roomId, onOpenDocument, defaultExpanded }: ReviewProps) {
  const { suggestions, decisions } = useSellerSuggestions(roomId);
  return (
    <VersionSuggestionsReviewCard
      suggestions={suggestions}
      decisions={decisions}
      onOpenDocument={onOpenDocument}
      defaultExpanded={defaultExpanded}
      titleAside={<HelpTip id="seller.documents.possible-versions" />}
      mergeAside={<HelpTip id="seller.documents.merge-keep" />}
    />
  );
}

interface PanelProps {
  roomId: string;
  /** The document in the details drawer: only its suggestions show. */
  documentId: string;
  /** Open a document in the details drawer. */
  onOpenDocument?: (documentId: string) => void;
  className?: string;
}

/**
 * The suggestions involving ONE document, in the details drawer: the same
 * compact rows, with Merge / Keep separate. Renders nothing while loading, on an
 * error, or when there is none.
 */
export function VersionSuggestionsPanel({
  roomId,
  documentId,
  onOpenDocument,
  className,
}: PanelProps) {
  const { suggestions, decisions } = useSellerSuggestions(roomId);
  return (
    <DocumentVersionSuggestions
      suggestions={suggestions}
      documentId={documentId}
      decisions={decisions}
      onOpenDocument={onOpenDocument}
      className={className}
    />
  );
}
